import { lazy, Suspense, useEffect, useRef } from 'react';
import { Alert, Skeleton } from '@/ui/primitives';
import { CepForm } from '@/components/CepForm';
import { NotifyInline } from '@/components/NotifyInline';
import { ResultPanel } from '@/components/ResultPanel';
import { StationList } from '@/components/StationList';
import { neededHeight } from '@/domain/embedHeight';
import type { StationFinder } from '@/hooks/useStationFinder';
import { notifyHostOfHeight } from '@/services/embedHost';

const StationMap = lazy(() =>
  import('@/components/StationMap').then((module) => ({ default: module.StationMap })),
);

/* Two arrangements, one breakpoint at 640px.

   Wide, the map fills the whole embed and the panel floats over it as a card. The section
   is a horizontal band, so a panel taking a column of it left the map at little over half
   the width it could have — and the map is what the section is for. A card over the map is
   also how every map product does this, so it needs no explaining.

   Narrow, the card would cover the map it sits on, so the map is a band and the panel sits
   under it in the flow. */
const STACKED_MAP_HEIGHT = 'h-[clamp(200px,60vw,300px)] sm:absolute sm:inset-0 sm:h-auto';

const FLOATING_PANEL =
  'sm:absolute sm:left-4 sm:top-4 sm:z-10 sm:w-[360px] sm:max-h-[calc(100%-2rem)] ' +
  'sm:overflow-y-auto sm:rounded-xl sm:border sm:border-grey-200 sm:bg-white/95 ' +
  'sm:shadow-lg sm:backdrop-blur-sm';

/* The host decides the height. On the vammo.com home the section stretches to whatever the
   video and the copy beside it need, which can be far more than this page's own content —
   measured at 736px against 460px of content, leaving 276px of white below the map inside
   the frame. So the root paints the whole frame it was given, with a floor for the case
   where the host gives it almost nothing. */
const FILLS_THE_FRAME = 'relative min-h-[max(460px,100dvh)]';

/* The page inside the vammo.com home, in place of the pit stop section. It carries no
   heading and no language switch: the section around it already has a heading, and the
   host page is written in one language, which reaches this page as `lang`. */
export const EmbedLayout = ({
  locale,
  dict,
  stations,
  state,
  result,
  errorKind,
  submitCep,
  useLocation,
  mapFailed,
  onMapUnsupported,
  nearbyStations,
  notifyIntent,
  submitNotify,
}: StationFinder) => {
  const root = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);

  /* The host gives this page a band of its own height, and the longest answer does not fit
     in it. Rather than cut the answer, report what the content needs and let the host grow
     the frame.

     Measured off the content wrapper, never off the root: the root now fills the frame, so
     reading it would report the frame's height back to the host, which would grow the frame
     again on every pass and leave the section stuck at its tallest state. */
  useEffect(() => {
    const rootElement = root.current;
    const contentElement = content.current;
    if (!rootElement || !contentElement) {
      return;
    }
    const report = () => {
      const panel = contentElement.parentElement;
      const panelStyle = panel === null ? null : getComputedStyle(panel);
      notifyHostOfHeight(
        neededHeight({
          rootTop: rootElement.getBoundingClientRect().top,
          contentBottom: contentElement.getBoundingClientRect().bottom,
          paddingBottom: panelStyle === null ? 0 : parseFloat(panelStyle.paddingBottom),
          /* Read off the stylesheet rather than repeating the breakpoint here. A second
             copy of 640px in JavaScript is a copy that drifts. */
          floating: panelStyle?.position === 'absolute',
        }),
      );
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(contentElement);
    observer.observe(rootElement);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={root}
      className={`flex w-full flex-col bg-white text-black ${FILLS_THE_FRAME}`}
    >
      {mapFailed ? null : (
        <div className={`relative w-full shrink-0 ${STACKED_MAP_HEIGHT}`}>
          <Suspense fallback={<Skeleton className="absolute inset-0 rounded-none" />}>
            <StationMap
              surface="embed"
              dict={dict}
              stations={stations}
              origin={result?.coords}
              nearby={nearbyStations}
              onUnsupported={onMapUnsupported}
            />
          </Suspense>
        </div>
      )}

      <div className={`w-full p-4 ${FLOATING_PANEL}`}>
        {/* The panel stretches to the frame; this wrapper does not. It is what the height
            report measures, and measuring anything the host sized would report the frame
            back to the host and grow it again on every pass. */}
        <div ref={content} className="flex flex-col gap-3">
        {result ? null : (
          <p className="text-[13px] leading-snug text-grey-800">{dict.hero.subtitle}</p>
        )}

        <CepForm
          variant="compact"
          dict={dict}
          onSubmit={submitCep}
          onUseLocation={useLocation}
          isBusy={state === 'busy'}
        />

        {state === 'error' && errorKind ? (
          <Alert variant={errorKind === 'imprecise' ? 'warning' : 'destructive'}>
            {dict.errors[errorKind]}
          </Alert>
        ) : null}

        {result ? (
          <ResultPanel
            /* Remount per answer: without a changing key the reveal plays once and every
               later lookup appears with no transition at all. */
            key={`${result.cep ?? 'geo'}-${result.nearest.address}`}
            density="compact"
            dict={dict}
            locale={locale}
            result={result}
            action={
              notifyIntent === null ? undefined : (
                <NotifyInline
                  dict={dict}
                  intent={notifyIntent}
                  band={result.band}
                  inputType={result.cep === null ? 'geolocation' : 'cep'}
                  onSubmit={submitNotify}
                />
              )
            }
          />
        ) : null}

        {/* The list stands in for the map only where the browser cannot draw one, and only
            once there is something to list. */}
        {mapFailed && result ? (
          <StationList dict={dict} locale={locale} stations={nearbyStations} isMapFallback />
        ) : null}

        {/* No privacy notice here. The reader is on the vammo.com home, which carries its
            own policy, and a second narrower one inside a section of it reads as a
            different promise. What leaves this page did not change, so the obligation
            moved to the host rather than disappearing — `docs/embed-in-vammo-com.md`
            hands it over. */}
        </div>
      </div>
    </div>
  );
};
